import React, { useEffect, useRef, useState } from 'react';
import { Btn, KPI, KPIRow, Loading, Msg, Section, panelPost } from '../../kit';
import { Check, Labeled, inputCls } from '../emailKit';
import {
  Banner, ConfirmDialog, Flash, Overview, SECTION, ServerWaitBanner, Settings as SettingsData, Trigger, fmtIST, num,
  waitingForServer, windowText,
} from './shared';
import { contentText } from './Switches';

/**
 * MEMAC → Settings (app_settings key "memac", email/memac_settings_set): the
 * global switch, the send window, the interval, the status cap and the
 * per-run limit. The env gate MEMAC_AUTO is the server's and only shown.
 *
 * The global switch is not part of the form: turning it ON is its own
 * button and confirm, which lists exactly which triggers would then send;
 * turning it off is immediate. A form save never sends `enabled`, so it can
 * never flip the switch by accident.
 *
 * That list comes from the memac_triggers read, so "Turn MEMAC on" stays
 * disabled until that read has succeeded: while it loads, or after it
 * failed, the list would be empty and the confirm would say nothing is sent
 * and skip its acknowledgement.
 */

type Draft = { interval: string; from: string; to: string; cap: string; perRun: string; excludeUnsub: boolean };
const draftFrom = (s: SettingsData | null): Draft => ({
  interval: s?.interval_minutes === null || s?.interval_minutes === undefined ? '' : String(s.interval_minutes),
  from: s?.window ? String(s.window[0]) : '',
  to: s?.window ? String(s.window[1]) : '',
  cap: s?.status_cap_30d === null || s?.status_cap_30d === undefined ? '' : String(s.status_cap_30d),
  perRun: s?.max_per_run === null || s?.max_per_run === undefined ? '' : String(s.max_per_run),
  excludeUnsub: s ? s.exclude_unsubscribed : true,
});

const whole = (label: string, v: string, lo: number, hi: number): number => {
  const n = Number(v.trim());
  if (v.trim() === '' || !Number.isInteger(n) || n < lo || n > hi) throw new Error(`${label} must be a whole number from ${lo} to ${hi}.`);
  return n;
};

const SettingsTab: React.FC<{
  ov: Overview | null; loading: boolean; error: string; triggers: Trigger[];
  /** The memac_triggers read: true only once it has succeeded; its error. */
  triggersReady: boolean; triggersError: string;
  onChanged: () => void; onOpenTrigger: (id: string) => void;
}> = ({ ov, loading, error, triggers, triggersReady, triggersError, onChanged, onOpenTrigger }) => {
  const s = ov?.settings || null;
  const [note, setNote] = useState<Flash>(null);
  const [draft, setDraft] = useState<Draft>(draftFrom(s));
  const [dirty, setDirty] = useState(false);
  const seeded = useRef<any>(s);
  useEffect(() => {
    if (!s || dirty || seeded.current === s) return;
    seeded.current = s;
    setDraft(draftFrom(s));
  }, [s, dirty]);
  const edit = (p: Partial<Draft>) => { setDraft(d => ({ ...d, ...p })); setDirty(true); };
  const [busy, setBusy] = useState('');
  const [ask, setAsk] = useState<'' | 'on' | 'unsub' | 'run'>('');
  const [askErr, setAskErr] = useState('');

  const autoOn = triggers.filter(t => !t.external && t.enabled && t.mode === 'auto');

  const post = async (body: Record<string, any>, ok: string) => {
    setBusy('save'); setAskErr('');
    const { error: e } = await panelPost(SECTION, 'memac_settings_set', body);
    setBusy('');
    if (e) { setAskErr(e); setNote({ kind: 'error', text: e }); return false; }
    setNote({ kind: 'ok', text: ok });
    onChanged();
    return true;
  };

  const buildForm = (): Record<string, any> => ({
    interval_minutes: whole('The interval', draft.interval, 5, 1440),
    send_window_ist: (() => {
      const a = whole('The window start', draft.from, 0, 23);
      const b = whole('The window end', draft.to, 1, 24);
      if (b <= a) throw new Error('The send window must end after it starts.');
      return [a, b];
    })(),
    status_cap_30d: whole('The status cap', draft.cap, 1, 50),
    max_per_run: whole('The per-run limit', draft.perRun, 1, 2000),
    exclude_unsubscribed: draft.excludeUnsub,
  });
  const saveForm = async (confirmed = false) => {
    let body: Record<string, any>;
    try { body = buildForm(); } catch (e: any) { setNote({ kind: 'error', text: e.message }); return; }
    // Letting unsubscribed members back in widens who gets mail: ask first.
    if (!body.exclude_unsubscribed && s?.exclude_unsubscribed !== false && !confirmed) { setAsk('unsub'); return; }
    if (await post({ ...body, ...(confirmed ? { confirm: true } : {}) }, 'Settings saved. The global switch is unchanged.')) {
      setDirty(false); setAsk('');
    }
  };

  const turnOn = async () => {
    if (await post({ enabled: true, confirm: true }, 'MEMAC global switch is ON.')) setAsk('');
  };
  const turnOff = async () => { await post({ enabled: false }, 'MEMAC global switch is OFF — nothing is sent automatically from the next run.'); };

  const runAll = async () => {
    setBusy('run'); setAskErr('');
    const { data, error: e } = await panelPost(SECTION, 'memac_run_now', {});
    setBusy('');
    if (e) { setAskErr(e); return; }
    setAsk('');
    // memac.process_due: {sent, failed, skipped?, error?, triggers: [{trigger_id, sent, failed, …}]}
    const skipped = data?.skipped || data?.error;
    if (skipped) {
      setNote({ kind: 'error', text: `Nothing was sent: ${String(skipped)}.` });
      onChanged();
      return;
    }
    const res: any[] = Array.isArray(data?.triggers) ? data.triggers : [];
    const sent = Number(data?.sent ?? 0);
    setNote({ kind: 'ok', text: `Run finished: ${num(sent)} e-mail${sent === 1 ? '' : 's'} sent${data?.failed ? `, ${num(data.failed)} failed` : ''}${res.length ? ` across ${res.length} trigger${res.length === 1 ? '' : 's'}` : ''}.` });
    onChanged();
  };

  if (loading && !ov) return <Loading />;
  const onLines: React.ReactNode[] = !autoOn.length ? [
    <>Nothing is sent yet: no trigger is On with mode Auto. From then on, any trigger someone switches to On + Auto
      (which asks again) sends on the next run.</>,
  ] : [
    <>{ov?.envAuto ? 'On the next run' : 'Once the server gate is open'}, MEMAC starts e-mailing members automatically for <b>{autoOn.length} trigger{autoOn.length === 1 ? '' : 's'}</b>:</>,
    ...autoOn.map(t => <><b>{t.id} · {t.name}</b> — {contentText(t)}
      {t.would_match !== null ? `; ${num(t.would_match)} would match now` : '; not counted yet'}.</>),
  ];
  onLines.push(
    s ? <>Runs every {s.interval_minutes ?? '?'} minutes, {windowText(s)}; at most {num(s.max_per_run ?? 0)} e-mails per run;
      at most {s.status_cap_30d ?? 6} non-service mails per member per 30 days.</> : null,
    ov && !ov.envAuto ? <>The server setting MEMAC_AUTO=1 is <b>missing</b>, so nothing is sent until it is set on the
      server and the API is restarted — this switch only takes effect then.</> : null,
    <>Turning it off again stops everything from the next run.</>,
  );

  // Why "Turn MEMAC on" cannot be pressed yet ('' = it can).
  const onBlock = !ov ? 'The MEMAC status has not been read'
    : triggersError ? 'The trigger list could not be read — its confirm must list every On + Auto trigger'
      : !triggersReady ? 'Reading the trigger list…' : '';

  return (
    <div className="flex flex-col gap-4">
      {error && <Msg kind="error">MEMAC settings could not be read: {error}</Msg>}
      {triggersError && (
        <Msg kind="error">
          The trigger list could not be read: {triggersError}. “Turn MEMAC on” stays disabled until it reads, because
          its confirm has to list every trigger that would start sending.{' '}
          <button type="button" className="underline underline-offset-2" onClick={onChanged}>Try again</button>
        </Msg>
      )}
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
      {ov && triggersReady && waitingForServer(ov, autoOn.length) && (
        <ServerWaitBanner ov={ov} ids={autoOn.map(t => t.id)} onOpenTrigger={onOpenTrigger} />
      )}

      <Section title="Global switch">
        <KPIRow>
          <KPI label="Global switch" value={!s ? 'Unknown' : s.enabled ? 'On' : 'Off'} hint="turned on and off here" />
          <KPI label="Server setting (read-only)" value={!ov ? 'Unknown' : ov.envAuto ? 'Set' : 'Not set'}
            hint="MEMAC_AUTO=1 on the server; applies after an API restart" />
          <KPI label="On + Auto triggers" value={triggersReady ? num(autoOn.length) : '—'} hint="each switched on separately" />
          <KPI label="Scheduler" value={ov?.scheduler.running === null || ov?.scheduler.running === undefined ? '—' : ov.scheduler.running ? 'Running' : 'Stopped'}
            hint={`last ${fmtIST(ov?.scheduler.last_run_at)} · next ${fmtIST(ov?.scheduler.next_run_at)}`} />
        </KPIRow>
        <p className="text-xs text-grayscale-1100">
          Automatic MEMAC mail goes out only when all three are in place: the server setting MEMAC_AUTO=1 (set on the
          server; an API restart applies it), this switch, and the trigger's own On + Auto. Manual sends ("Send to these
          N now") and tests do not depend on this switch.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {s?.enabled
            ? <Btn kind="danger" onClick={turnOff} disabled={busy !== ''}>{busy === 'save' ? 'Working…' : 'Turn MEMAC off'}</Btn>
            : <Btn kind="primary" onClick={() => { setAskErr(''); setAsk('on'); }} disabled={busy !== '' || onBlock !== ''}
              title={onBlock || undefined}>Turn MEMAC on…</Btn>}
          <Btn onClick={() => { setAskErr(''); setAsk('run'); }} disabled={busy !== '' || !triggersReady || !autoOn.length || !ov?.envAuto}
            title={!ov?.envAuto ? 'The server setting MEMAC_AUTO=1 is missing — a run would send nothing'
              : !triggersReady ? 'The trigger list has not been read'
              : !autoOn.length ? 'No trigger is On + Auto' : 'Runs every On + Auto trigger once now'}>
            Run all automatic triggers now…
          </Btn>
        </div>
        {autoOn.length > 0 && (
          <div className="text-[11px] text-grayscale-1100">
            On + Auto: {autoOn.map((t, i) => <React.Fragment key={t.id}>{i > 0 && ', '}
              <button type="button" className="underline underline-offset-2" onClick={() => onOpenTrigger(t.id)}>{t.id}</button>
            </React.Fragment>)}
          </div>
        )}
      </Section>

      <Section title="Limits and timing">
        <div className="grid md:grid-cols-3 gap-3">
          <Labeled label="Run every (minutes)" hint="5–1440">
            <input value={draft.interval} inputMode="numeric" onChange={e => edit({ interval: e.target.value })} className={inputCls} />
          </Labeled>
          <Labeled label="Send window, IST" hint="from hour – to hour">
            <span className="flex items-center gap-1.5">
              <input value={draft.from} inputMode="numeric" onChange={e => edit({ from: e.target.value })} className={`${inputCls} !w-20`} aria-label="Window start hour" />
              <span>to</span>
              <input value={draft.to} inputMode="numeric" onChange={e => edit({ to: e.target.value })} className={`${inputCls} !w-20`} aria-label="Window end hour" />
            </span>
          </Labeled>
          <Labeled label="Status cap" hint="non-service mails per member, rolling 30 days">
            <input value={draft.cap} inputMode="numeric" onChange={e => edit({ cap: e.target.value })} className={inputCls} />
          </Labeled>
          <Labeled label="Max e-mails per run" hint="across all triggers">
            <input value={draft.perRun} inputMode="numeric" onChange={e => edit({ perRun: e.target.value })} className={inputCls} />
          </Labeled>
        </div>
        <Check checked={draft.excludeUnsub} onChange={v => edit({ excludeUnsub: v })}>
          Also leave out members who unsubscribed through SendGrid (non-service mail)
        </Check>
        <p className="text-[11px] text-grayscale-1100">
          Members who used a MEMAC unsubscribe link are always left out of that category's mail — MEMAC and
          Campaigns alike — whatever this box says. The status cap counts MEMAC mails and campaigns; service mails
          are exempt.
        </p>
        <div className="flex items-center gap-2">
          <Btn kind="primary" onClick={() => saveForm()} disabled={!dirty || busy !== ''}>{busy === 'save' ? 'Saving…' : 'Save settings'}</Btn>
          {dirty && <Btn onClick={() => { setDraft(draftFrom(s)); setDirty(false); }}>Discard changes</Btn>}
        </div>
      </Section>

      {ask === 'on' && !onBlock && (
        <ConfirmDialog title="Turn the MEMAC global switch on?" danger={autoOn.length > 0} busy={busy !== ''} error={askErr}
          confirmLabel={autoOn.length ? 'Turn on — start automatic sending' : 'Turn on'}
          ack={autoOn.length ? 'I understand members will get these e-mails without anyone pressing Send.' : undefined}
          onClose={() => setAsk('')} onConfirm={turnOn} lines={onLines} />
      )}
      {ask === 'unsub' && (
        <ConfirmDialog title="Stop leaving out SendGrid unsubscribes?" danger busy={busy !== ''} error={askErr}
          confirmLabel="Save — include SendGrid unsubscribes"
          ack="I understand people who unsubscribed through SendGrid may get MEMAC mail again."
          onClose={() => setAsk('')} onConfirm={() => saveForm(true)}
          lines={[
            <>Members who unsubscribed through SendGrid (an unsubscribe or group unsubscribe event) will get MEMAC
              mail again — by hand and, where a trigger is On + Auto, automatically.</>,
            <>Members who used a MEMAC unsubscribe link, bounces and spam reports are still left out.</>,
          ]} />
      )}
      {ask === 'run' && triggersReady && (
        <ConfirmDialog title="Run every automatic trigger now?" danger busy={busy !== ''} error={askErr}
          confirmLabel="Run now — send real e-mails"
          ack="I understand this e-mails every eligible member now."
          onClose={() => setAsk('')} onConfirm={runAll}
          lines={[
            <>Runs once now for the {autoOn.length} On + Auto trigger{autoOn.length === 1 ? '' : 's'}:{' '}
              {autoOn.map(t => t.id).join(', ')} — real e-mails to everyone each one finds eligible.</>,
            <>Caps, exclusions and the server gate still apply{s?.max_per_run ? `; at most ${num(s.max_per_run)} e-mails` : ''}.
              Only the global switch is not waited for.</>,
          ]} />
      )}
      {!s && !error && <Banner tone="info">Settings arrive with the MEMAC status.</Banner>}
    </div>
  );
};

export default SettingsTab;
